/** Smoothness control for freehand construction lines (issue #171).
 *
 *  When a user draws a freehand construction line, every pointer
 *  sample lands in `ConstructionLine.rawPoints`. The visible
 *  `pathData` is a derived view: at smoothness=0 it's the raw
 *  polyline; at smoothness>0 it's the bezier-fitted path Paper.js
 *  produces from `path.simplify(tolerance)`.
 *
 *  Mirrors the shape of `constructionRounding.ts` — a single
 *  derive-pass that runs on every commit, returns the input
 *  unchanged when nothing needs updating, and lives next to its
 *  source on the line itself.
 *
 *  Lifecycle: `rawPoints` + `smoothness` are dropped on the first
 *  destructive geometry edit (vertex drag, handle drag, curve
 *  bend) — see `dropConstructionPrimitive`, which strips them
 *  alongside the primitive metadata. Undo restores the pre-edit
 *  snapshot. */

import type paper from "paper";
import type { ConstructionLine, Project, Vec2 } from "./Project";
import { projectConstructionLines } from "./Project";

/**
 * The smoothing tolerance a freshly-traced object is seeded with, and the top of
 * every Smoothness control — SHARED by construction lines and leather geometry,
 * because both are project millimetres and there is no reason the same gesture
 * should mean different amounts in the two.
 *
 * This used to be 2.5, which is Paper's own `simplify` default and assumes PIXEL
 * space. At mm scale that is enormous: measured on a traced 30 mm circle,
 * tolerance 2.5 collapses 60 anchors to 2 and throws away 55% of the shape's
 * AREA. 0.3 holds the area to within 0.3% while still dropping 60 anchors to 28,
 * and 1 is the ceiling because past it the fit stops tracking the trace at all
 * (3 anchors, a third of the area gone).
 */
export const DEFAULT_FREEHAND_SMOOTHNESS = 0.3;
export const MAX_FREEHAND_SMOOTHNESS = 3;

/**
 * Smoothness is shown as a PERCENTAGE of the maximum, which is the number a
 * maker can reason about — "a bit" vs "a lot" — where a bare tolerance in
 * millimetres is a paper.js implementation detail. 0% is the raw trace and 100%
 * is tolerance {@link MAX_FREEHAND_SMOOTHNESS}.
 *
 * The MODEL stays in tolerance units: that's what `simplify` takes, it's what
 * saved projects carry, and converting only at the UI edge means the geometry
 * code never has to care how the control is labelled.
 *
 * Worth knowing when reading the top of the range: fidelity falls off fast.
 * On a traced 30 mm circle, 10% (the default) holds the area to within 0.3%,
 * ~33% loses about 29%, and the top of the range flattens the shape almost
 * entirely. The range goes there deliberately — it's a choice the user gets to
 * make — but the useful working band is the bottom tenth or so.
 */
export function smoothnessPercent(tolerance: number): number {
  return Math.round((tolerance / MAX_FREEHAND_SMOOTHNESS) * 100);
}

export function smoothnessFromPercent(percent: number): number {
  return (percent / 100) * MAX_FREEHAND_SMOOTHNESS;
}

/** Re-derive `pathData` from `rawPoints` + `smoothness` for every
 *  construction line that carries them. Returns the input project
 *  unchanged when no line needs an update so callers can cheaply
 *  skip re-snapshotting. */
export function deriveFreehandSmoothing(
  project: Project,
  scope: paper.PaperScope,
): Project {
  const lines = projectConstructionLines(project);
  if (lines.length === 0) return project;
  let changed = false;
  const next: ConstructionLine[] = [];
  for (const line of lines) {
    // A hand-edited line keeps its trace so the Smoothness slider stays
    // usable (#248), but stands down from the automatic re-derive — running
    // it would rebuild the geometry from the trace and undo the edit.
    if (!line.rawPoints || line.rawPoints.length < 2 || line.freehandEdited) {
      next.push(line);
      continue;
    }
    const closed = isClosedPathData(line.pathData);
    const derived = smoothPathFromRawPoints(
      line.rawPoints,
      line.smoothness ?? 0,
      closed,
      scope,
    );
    if (derived === line.pathData) {
      next.push(line);
      continue;
    }
    next.push({ ...line, pathData: derived });
    changed = true;
  }
  if (!changed) return project;
  return { ...project, constructionLines: next };
}

/** Public helper so the freehand commit code in ConstructionTool can
 *  seed `pathData` with the same shape the derive pass will produce
 *  — keeping the very first commit consistent with every subsequent
 *  re-derive. */
export function smoothPathFromRawPoints(
  rawPoints: Vec2[],
  smoothness: number,
  closed: boolean,
  scope: paper.PaperScope,
): string {
  if (rawPoints.length < 2) return "";
  if (smoothness <= 0) return pointsToPolyline(rawPoints, closed);
  const path = new scope.Path();
  for (const p of rawPoints) path.add(new scope.Point(p.x, p.y));
  path.closed = closed;
  path.simplify(smoothness);
  const out = path.pathData;
  path.remove();
  return out && out.length > 0 ? out : pointsToPolyline(rawPoints, closed);
}

/** `M x y L x y L x y …` (with an optional trailing ` Z`). Same
 *  shape as `pathFromBuffer` in `canvas/Scene.ts` so the
 *  smoothness=0 fast path is bit-identical to what a freshly-drawn
 *  freehand line would produce without simplification. */
export function pointsToPolyline(points: Vec2[], closed: boolean): string {
  if (points.length === 0) return "";
  const [first, ...rest] = points;
  let d = `M${first!.x} ${first!.y}`;
  for (const p of rest) d += ` L${p.x} ${p.y}`;
  if (closed) d += " Z";
  return d;
}

/** A single-subpath freehand line is closed iff its serialized
 *  `pathData` ends with a `Z` (or `z`) command. Robust enough for
 *  the freehand pipeline (which only ever produces one subpath);
 *  multi-subpath lines never carry `rawPoints` because they can't
 *  come out of the freehand tool. */
function isClosedPathData(pathData: string): boolean {
  return /[Zz]\s*$/.test(pathData);
}
